import type { GridDataSource, GridSortModel } from "@mui/x-data-grid-premium";
import { useCallback, useEffect, useState } from "react";
import type { FailedMeasure } from "../../../../../../../../api/types/svTypes.ts";
import useFetch from "@entrilia/shared/hooks/useFetch.ts";
import {
  prepareListReport,
  type ListPageConfiguration,
  type ListPageFetcher,
  type ListReportFailures,
} from "../utils/prepareListReport.ts";

const emptyFailures: FailedMeasure[] = [];
const emptyDataSource: GridDataSource = { getRows: async () => ({ rows: [], rowCount: 0 }) };

const useListReportDataSource = <P extends object>(
  configuration: ListPageConfiguration<P>,
  sortModel: GridSortModel,
  fetchRows: ListPageFetcher<P>,
  disabled = false
) => {
  const [pageFailures, setPageFailures] = useState<ListReportFailures>();
  const loadReport = useCallback(
    (signal?: AbortSignal) =>
      prepareListReport(configuration, sortModel, fetchRows, signal ?? new AbortController().signal, setPageFailures),
    [configuration, sortModel, fetchRows]
  );
  const [report, error, { isFetching, fetch: onRefresh }] = useFetch(loadReport, { disabled });

  useEffect(() => {
    report?.activate();
    return () => report?.dispose();
  }, [report]);

  return {
    resultId: report?.resultId,
    failedMeasures:
      (report && pageFailures?.resultId === report.resultId ? pageFailures.failedMeasures : report?.failedMeasures) ??
      emptyFailures,
    error: error?.message,
    dataSource: report?.dataSource ?? emptyDataSource,
    totalRows: report?.totalRows,
    isLoading: isFetching,
    configuration: report?.configuration ?? configuration,
    sortModel: report?.sortModel ?? sortModel,
    onRefresh,
  };
};

export default useListReportDataSource;
